import { VocalLib, calibrationMidi, type CaptureDiagnosis } from '@tunasong/music-lib'
import { runCaptureWindow, type CaptureWindowDeps } from './capture-session.js'
import { calibratedRange, recordCalibration, type CalibrationState } from './pitch-calibration.js'
import { leadInMs } from './pitch-training-playback.js'

/**
 * One take of the two-take range calibration (§5.8): listen, measure, fold into the state.
 *
 * Shared, because two tools measure the same voice. The Pitch Trainer needs the range before it
 * can generate an exercise; the Melody Trainer only needs it to move an existing melody to fit -
 * but "how long a calibration take listens", "what counts as a measured note" and "when the range
 * is complete" are facts about the singer's voice, not about either tool. A second copy would be a
 * second set of those rules, and the two would drift.
 *
 * Everything that touches audio is injected, so the fold is testable without a microphone; the
 * caller keeps what is genuinely its own - its state machine, and where the result is stored.
 *
 * Spec: docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §5.8, §9
 */

/** How long a calibration take listens - §5.8 asks for a sustained second, with room to settle. */
const CALIBRATION_CAPTURE_MS = 2500

export type CalibrationTakeDeps = Pick<CaptureWindowDeps, 'setMuted' | 'startFrames' | 'wait' | 'onFrame'>

export interface CalibrationTakeOutcome {
  /** The calibration after this take. A rejected take advances nothing (see `recordCalibration`). */
  next: CalibrationState
  /** Why the take heard nothing, when it heard nothing. */
  diagnosis: CaptureDiagnosis | null
  /** The range to store, or null while the two takes are not both in. */
  range: VocalLib.VocalRange | null
}

export const runCalibrationTake = async (
  deps: CalibrationTakeDeps,
  calibration: CalibrationState
): Promise<CalibrationTakeOutcome> => {
  const { setMuted, startFrames, wait, onFrame } = deps

  const frames = await runCaptureWindow(
    {
      setMuted,
      startFrames,
      /** Nothing to audition - the singer is being measured, not tested. */
      playReference: async () => undefined,
      playCountIn: async () => undefined,
      wait,
      onFrame,
    },
    { leadInMs, captureMs: CALIBRATION_CAPTURE_MS }
  )

  const measuredMidi = calibrationMidi(frames)
  const next = recordCalibration(calibration, measuredMidi)

  return {
    next,
    diagnosis: measuredMidi === null ? VocalLib.diagnoseCapture(frames) : null,
    range: next.step === 'done' ? calibratedRange(next) : null,
  }
}
